import $ from "jquery";
import UI from "~/ui";
import * as validation from "~/utils/validation";
import { generaRecaptcha, recaptchaReady, recaptchaToken } from "~/recaptcha";
import { subscribeNewsletterWithConfirm } from "~/mailup";
import { ConsensiHelper } from "~/services/ConsensiHelper";
import { SchedaVenditaState, ApiResponse } from "./SchedaVenditaState";

/**
 * Configurazione riutilizzabile per un form di contatto (Info Scheda o Richiedi Visita).
 */
export interface FormContattiConfig {
    /** Selettore CSS del form */
    formSelector: string;
    /** id del pulsante che apre la modale (event delegation) */
    openModalId: string;
    /** id del pulsante di submit */
    submitButtonId: string;
    /** id della modale bootstrap */
    modalId: string;
    /** id del reCAPTCHA (es. "infoscheda" | "richiedivisita") */
    recaptchaKey: string;
    /** Endpoint WebApi relativo (append a BaseUrlWebApi) */
    endpoint: string;
    /** Suffisso comune degli id dei campi (es. "info-scheda" | "richiedi-visita") */
    fieldSuffix: string;
    /** Callback opzionale eseguita all'apertura della modale (dopo il precompilamento) */
    onOpen?: (state: SchedaVenditaState) => void;
    /** Messaggio di errore generico in caso di eccezione durante l'invio */
    genericErrorMessage: string;
    /** Testo/HTML pulsante durante l'invio */
    sendingHtml?: string;
}

/**
 * Manager parametrico che gestisce validazione, precompilazione, invio e reset
 * del form di contatto. Un'istanza per ciascun form.
 */
export class FormContattiManager {
    private readonly state: SchedaVenditaState;
    private readonly config: FormContattiConfig;
    private readonly form: HTMLFormElement;

    private recaptchaGenerated: boolean = false;
    private isSending: boolean = false;

    constructor(state: SchedaVenditaState, config: FormContattiConfig) {
        this.state = state;
        this.config = config;
        this.form = document.querySelector(config.formSelector) as HTMLFormElement;
    }

    public register(): void {
        if (!this.form) {
            return;
        }

        this.setupValidation();

        // Apertura modale
        $("#" + this.config.openModalId).on("click", (e) => {
            e.preventDefault();
            void this.initRecaptcha();
            this.resetFormState();
            this.precompila();
            if (this.config.onOpen) {
                this.config.onOpen(this.state);
            }
        });

        // Reset flag su chiusura modale
        $("#" + this.config.modalId).on("hidden.bs.modal", () => {
            this.isSending = false;
            $("#" + this.config.submitButtonId).prop("disabled", false);
        });

        // Submit
        $("#" + this.config.submitButtonId).on("click", (e) => {
            e.preventDefault();
            if (this.isSending) {
                return false;
            }
            void this.handleSubmit();
        });
    }

    private async initRecaptcha(): Promise<void> {
        try {
            await recaptchaReady();
            generaRecaptcha(this.config.recaptchaKey, true);
            this.recaptchaGenerated = true;
        } catch (error) {
            console.error(`Errore durante la generazione del reCAPTCHA ${this.config.recaptchaKey}:`, error);
            this.recaptchaGenerated = false;
        }
    }

    private precompila(): void {
        const ui = this.state.userInfo;
        if (!(ui?.isAuthenticated && ui?.anagraficaId)) {
            return;
        }

        const suffix = this.config.fieldSuffix;
        const nome = ui.nome || ui.Nome || "";
        const cognome = ui.cognome || ui.Cognome || "";
        const email = ui.email || ui.Email || "";

        $(`#nome-${suffix}`).val(nome);
        $(`#cognome-${suffix}`).val(cognome);
        $(`#email-${suffix}`).val(email);

        const telefono = this.getTelefonoFromUserInfo();
        if (telefono) {
            $(`#telefono-${suffix}`).val(telefono);
        }

        const setCheckboxState = (codice: string, checkboxId: string) => {
            const consenso = ui.consensi?.find((c: { codice: string; concesso: boolean }) => c.codice === codice);
            $(`#${checkboxId}`).prop("checked", !!consenso?.concesso);
        };
        setCheckboxState("PRIVA", `privacy-${suffix}`);
        setCheckboxState("NEWS", `newsletter-${suffix}`);
        setCheckboxState("TERZI", `societapartner-${suffix}`);
    }

    private setupValidation(): void {
        const suffix = this.config.fieldSuffix;

        this.form.addEventListener("input", () => {
            this.form.classList.remove("was-validated");
        });

        const inputValidator = (inputElement: HTMLInputElement): validation.ValidationResult => {
            const value = $(inputElement).val() as string;
            const ok = value.length > 0;
            return { ok, error: ok ? "" : "Campo obbligatorio" };
        };

        const emailValidator = (inputElement: HTMLInputElement): validation.ValidationResult => {
            const email = $(inputElement).val() as string;
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const ok = emailRegex.test(email);
            return { ok, error: ok ? "" : "Formato email non valido" };
        };

        validation.setValidatorOnInput(`nome-${suffix}`, inputValidator);
        validation.setValidatorOnInput(`cognome-${suffix}`, inputValidator);
        validation.setValidatorOnInput(`email-${suffix}`, emailValidator);
        validation.setValidatorOnInput(`testo-${suffix}`, inputValidator);
    }

    private validateAll(): void {
        const suffix = this.config.fieldSuffix;
        const inputValidator = (inputElement: HTMLInputElement): validation.ValidationResult => {
            const value = $(inputElement).val() as string;
            const ok = value.length > 0;
            return { ok, error: ok ? "" : "Campo obbligatorio" };
        };
        const emailValidator = (inputElement: HTMLInputElement): validation.ValidationResult => {
            const email = $(inputElement).val() as string;
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const ok = emailRegex.test(email);
            return { ok, error: ok ? "" : "Formato email non valido" };
        };
        validation.validate(`nome-${suffix}`, inputValidator);
        validation.validate(`cognome-${suffix}`, inputValidator);
        validation.validate(`email-${suffix}`, emailValidator);
        validation.validate(`testo-${suffix}`, inputValidator);
    }

    private async handleSubmit(): Promise<void> {
        this.validateAll();
        if (!this.form.checkValidity()) {
            this.form.classList.add("was-validated");
            return;
        }

        if (!this.recaptchaGenerated) {
            UI.confirmOneButton("", "Errore: reCAPTCHA non generato");
            return;
        }
        if (!recaptchaToken) {
            UI.confirmOneButton("", "Il controllo di sicurezza di google è fallito, provare a ricaricare la pagina");
            return;
        }

        this.isSending = true;
        const $btnSubmit = $("#" + this.config.submitButtonId);
        const originalText = $btnSubmit.html();
        $btnSubmit.prop("disabled", true)
            .html(this.config.sendingHtml ?? '<span class="spinner-border spinner-border-sm me-2"></span>Invio in corso...');

        const formData: Record<string, string> = {};
        $(this.config.formSelector).serializeArray().forEach((field) => {
            formData[field.name] = field.value;
        });

        try {
            const url = `${window.BaseUrlWebApi}${this.config.endpoint}`;
            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json; charset=utf-8" },
                body: JSON.stringify(formData)
            });
            const data: ApiResponse = await response.json();

            if (data.success) {
                if (this.state.isUserAuthenticated) {
                    await ConsensiHelper.saveAndSyncConsensi(this.config.formSelector, this.state.userInfo.email || "");
                } else if (formData["Newsletter"] === "true") {
                    await subscribeNewsletterWithConfirm(
                        window.ATP_ListID_Newsletter,
                        window.ATP_GroupId_FormContatto,
                        formData["Email"],
                        formData["Nome"],
                        formData["Cognome"],
                        formData["Telefono"]
                    );
                }

                this.form.reset();
                this.resetFormState();
                $("#" + this.config.modalId).modal("hide");

                UI.confirmOneButton(
                    "Richiesta inviata",
                    "E-mail correttamente inviata, sarete ricontattati prima possibile"
                );
                this.recaptchaGenerated = false;
            } else {
                const errorMessage = data.responseText || data.message || "Errore sconosciuto";
                $("#resultmessage").text(`Non è stato possibile inviare la E-mail (${errorMessage})`);
                UI.confirmOneButton("", `Errore: ${errorMessage}`);
                this.resetFormState();
                await generaRecaptcha(this.config.recaptchaKey, true);
            }
        } catch (error) {
            console.error(`Errore durante l'invio della richiesta ${this.config.recaptchaKey}:`, error);
            UI.confirmOneButton("", this.config.genericErrorMessage);
            await generaRecaptcha(this.config.recaptchaKey, true);
        } finally {
            this.isSending = false;
            $btnSubmit.prop("disabled", false).html(originalText);
        }
    }

    private resetFormState(): void {
        if (!this.form) return;
        this.form.classList.remove("was-validated");
        $(this.form).find("input, select, textarea").each(function () {
            $(this).removeClass("is-invalid is-valid");
        });
        $(".invalid-feedback").hide();
    }

    /**
     * Recupera dai contatti utente il numero di telefono (priorità cellulare CELL > telefono TEL).
     */
    private getTelefonoFromUserInfo(): string | null {
        const ui = this.state.userInfo;
        if (!ui?.contatti || !Array.isArray(ui.contatti)) {
            return null;
        }
        const cellulare = ui.contatti.find((c: any) => c.tipoContatto === "CELL" && c.valore);
        if (cellulare) return this.formatTelefono(cellulare);
        const telefono = ui.contatti.find((c: any) => c.tipoContatto === "TEL" && c.valore);
        if (telefono) return this.formatTelefono(telefono);
        return null;
    }

    private formatTelefono(contatto: any): string {
        if (!contatto || !contatto.valore) return "";
        if (contatto.prefisso && contatto.prefisso.trim() !== "") {
            return `${contatto.prefisso} ${contatto.valore}`;
        }
        return contatto.valore;
    }
}
